import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './sheet';
import { Button } from './button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/contexts/I18nContext';

interface PaymentMethod {
  id: 'alipay' | 'polar';
  name: string;
  description: string;
  iconPath: string;
  gradient: string;
}

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMethod: (methodId: 'alipay' | 'polar') => void;
  dismissible?: boolean;
  loadingMethod?: 'alipay' | 'polar' | null;
}

export const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onSelectMethod,
  dismissible = true,
  loadingMethod = null,
}) => {
  const { t } = useTranslation();

  const paymentMethods: PaymentMethod[] = [
    {
      id: 'alipay',
      name: t('purchase.alipayName'),
      description: t('purchase.alipayDescription'),
      iconPath: '/icons/alipay-icon.svg',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'polar',
      name: t('purchase.polarName'),
      description: t('purchase.polarDescription'),
      iconPath: '/icons/polar-icon.svg',
      gradient: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && dismissible && onClose()}>
      <SheetContent className="sm:max-w-lg bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-blue-900/20">
        <SheetHeader className="space-y-4 pb-6">
          <SheetTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t('purchase.selectPaymentMethod')}
          </SheetTitle>
          <SheetDescription className="text-gray-600 dark:text-gray-300 text-base">
            {t('purchase.selectPaymentDescription')}
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-8 space-y-4">
          {paymentMethods.map((method) => {
            const isLoading = loadingMethod === method.id;
            const isDisabled = loadingMethod !== null && !isLoading;

            return (
              <div key={method.id} className="group relative">
                {/* 发光效果 */}
                <div className={cn(
                  "absolute -inset-0.5 rounded-xl blur-sm opacity-0 group-hover:opacity-30 transition-all duration-300",
                  `bg-gradient-to-r ${method.gradient}`,
                  isDisabled && "opacity-0"
                )}></div>

                <Button
                  variant="outline"
                  onClick={() => !isDisabled && onSelectMethod(method.id)}
                  disabled={isDisabled}
                  className={cn(
                    "relative w-full p-6 h-auto flex items-center justify-start gap-4",
                    "bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm",
                    "border-2 border-gray-200 dark:border-gray-700",
                    "transition-all duration-300",
                    !isDisabled && "hover:border-transparent hover:shadow-lg transform hover:scale-[1.02]",
                    !isDisabled && `hover:bg-gradient-to-r hover:${method.gradient} hover:text-white`,
                    isDisabled && "opacity-50 cursor-not-allowed",
                    isLoading && "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                  )}
                >
                  <div className="w-12 h-12 flex items-center justify-center">
                    <img
                      src={method.iconPath}
                      alt={`${method.name} icon`}
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-lg mb-1">{method.name}</div>
                    <div className="text-sm opacity-80 leading-relaxed">
                      {isLoading ? t('purchase.creatingOrder') : method.description}
                    </div>
                  </div>
                  <div className="text-2xl opacity-60 group-hover:opacity-100 transition-opacity">
                    {isLoading ? (
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    ) : (
                      '→'
                    )}
                  </div>
                </Button>
              </div>
            );
          })}
        </div>
        
        {dismissible && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="ghost"
              onClick={onClose}
              className="w-full py-3 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {t('purchase.cancel')}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}; 